import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoped } from '@fortawesome/pro-light-svg-icons';
import {
  faChevronRight,
  faClock,
  faLocationArrow,
  faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { faMapPin } from '@fortawesome/pro-solid-svg-icons';
import { Text } from '@querodelivery/uni-react';
import { format } from '@querodelivery/qd-date-helper';
import type { MonitorDelivery } from '../../types';
import { formatDeliverymanName } from '../../utils';
import { DeliveryActions } from '../delivery-actions/delivery-actions';
import { DeliveryTags } from '../delivery-tags/delivery-tags';
import { DeliveryTypeIcon } from '../delivery-type-icon/delivery-type-icon';
import { ElapsedTime } from '../elapsed-time/elapsed-time';
import { TalkToDeliverymanButton } from '../talk-to-deliveryman/talk-to-deliveryman';
import styles from './delivery-card.module.scss';

const EMPTY_VALUE = '—';

interface DeliveryMonitorCardProps {
  delivery: MonitorDelivery;
  isSelected: boolean;
  onOpenDetail: (deliveryId: string) => void;
}

export function DeliveryMonitorCard({
  delivery,
  isSelected,
  onOpenDetail,
}: DeliveryMonitorCardProps) {
  const {
    code,
    isNew,
    isManual,
    deliveryType,
    place,
    deliveryman,
    currentStep,
    elapsedMinutes,
    slaLevel,
    distanceKm,
    createdAt,
  } = delivery;

  const placeLocation = [place.district, place.city].filter(Boolean).join(', ');

  return (
    <article
      className={styles.card}
      data-selected={isSelected}
      aria-current={isSelected || undefined}
      onClick={() => onOpenDetail(delivery._id)}
    >
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <span className={styles.identifier}>
            <DeliveryTypeIcon deliveryType={deliveryType} />
            <Text
              as="span"
              fontSize="sm"
              fontWeight="medium"
              lineHeight="lg"
              tone="supporting"
            >
              #{code}
            </Text>
          </span>
          <DeliveryTags isNew={isNew} isManual={isManual} />
        </div>
        <button
          type="button"
          className={styles.openDetail}
          data-monitor-delivery-id={delivery._id}
          aria-label={`Abrir detalhe da entrega #${code}`}
        >
          <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
        </button>
      </header>

      <div className={styles.placeRow}>
        <div className={styles.place}>
          <Text
            as="span"
            fontSize="sm"
            fontWeight="medium"
            lineHeight="lg"
            tone="secondary"
            truncate
          >
            {place.name}
          </Text>
          <span className={styles.placeLocation}>
            <FontAwesomeIcon
              icon={faLocationDot}
              className={styles.smallIcon}
            />
            <Text
              as="span"
              fontSize="sm"
              fontWeight="regular"
              lineHeight="default"
              tone="supporting"
              truncate
            >
              {placeLocation}
            </Text>
          </span>
        </div>

        <div className={styles.elapsedTime}>
          <ElapsedTime elapsedMinutes={elapsedMinutes} slaLevel={slaLevel} />
        </div>
      </div>

      {deliveryman && (
        <div className={styles.deliverymanRow}>
          <span className={styles.deliveryman}>
            <FontAwesomeIcon icon={faMoped} className={styles.mopedIcon} />
            <Text
              as="span"
              fontSize="xs"
              fontWeight="medium"
              lineHeight="sm"
              tone="supporting"
              truncate
            >
              {formatDeliverymanName(deliveryman.name)}
            </Text>
          </span>
          {currentStep && (
            <span className={styles.step}>
              <FontAwesomeIcon icon={faMapPin} className={styles.stepIcon} />
              <Text
                as="span"
                fontSize="xs"
                fontWeight="medium"
                lineHeight="lg"
                tone="supporting"
                truncate
              >
                {currentStep.label}
              </Text>
            </span>
          )}
        </div>
      )}

      <div className={styles.infoRow}>
        <span className={styles.info}>
          <FontAwesomeIcon
            icon={faLocationArrow}
            className={styles.smallIcon}
          />
          <Text
            as="span"
            fontSize="xs"
            fontWeight="medium"
            lineHeight="lg"
            tone="supporting"
          >
            {distanceKm === null ? EMPTY_VALUE : `${distanceKm.toFixed(1)} km`}
          </Text>
        </span>
        <span className={styles.info}>
          <FontAwesomeIcon icon={faClock} className={styles.smallIcon} />
          <Text
            as="span"
            fontSize="xs"
            fontWeight="medium"
            lineHeight="lg"
            tone="supporting"
          >
            {format(new Date(createdAt), 'HH:mm')}
          </Text>
        </span>
      </div>

      <div className={styles.footer} onClick={event => event.stopPropagation()}>
        {deliveryman ? (
          <TalkToDeliverymanButton phone={deliveryman.phone} />
        ) : (
          <DeliveryActions delivery={delivery} />
        )}
      </div>
    </article>
  );
}
